// File: js/money/dedupe.js
//
// Shared by every fold. Imports nothing (spec §8).
//
// Spec §8: "Every fold dedupes by id, because supabase-js truncates at 1000 rows
// silently and every read paginates on a stable key." A paginated read with an overlap
// window (and the 10-minute pull overlap of §7) legitimately returns the same row twice;
// the ledger id is the identity of a real-world sale (invariant 2), so a second copy is
// the SAME event, not a second one. Counting it twice is wrong money.

/**
 * First-occurrence-wins dedupe on the ledger `id`.
 *
 * Events with a nullish id pass through untouched: they cannot be keyed, and the folds
 * surface them as `missing-id` rather than dropping them here (invariant 6 — no silent
 * failure; a row must never vanish between the read and the total).
 *
 * @template {{id?: string}} T
 * @param {T[]} events
 * @returns {T[]} New array, input order preserved.
 * @throws {TypeError} If `events` is not an array (programmer error).
 */
export function dedupeById(events) {
    if (!Array.isArray(events)) {
        throw new TypeError(`dedupeById expects an array, received ${typeof events}`);
    }
    const seen = new Set();
    const unique = [];
    for (const event of events) {
        const id = event && event.id != null ? event.id : null;
        if (id === null) {
            unique.push(event);
            continue;
        }
        if (seen.has(id)) continue;
        seen.add(id);
        unique.push(event);
    }
    return unique;
}
